import type { CheckItem } from '@/content/checks'
import type { Photo } from '@/content/sections'
import { LINKS } from '@/content/pages/links'

/**
 * The Retail Industry Focus page — its copy and its figures, apart from the
 * component that draws them.
 *
 * **Why this cut has a page of its own.** `focus()` in `content/reports.ts` writes
 * every Industry Focus entry from one sentence template, and Retail was one of the
 * ten still carrying it: a two-paragraph series description under an amber note
 * saying the sector's own words had never been supplied. They were supplied on
 * 2026-08-28 — *"referans alarak ilgili sayfayı birebir tıpa tıp aynısını
 * tasarla"*, the same words the Tourism, Technology and Services cuts arrived in —
 * with a four-part full-page capture of digitopia.co's Retail Industry Focus page
 * as the reference, plus four files: the report mockup, Figure 1, and two
 * overlapping crops of the cover. So this is the twenty-fifth designed report page
 * and the tenth focus cut to arrive with **both** of its figures.
 *
 * **Where the words come from, band by band.** Everything here is transcribed from
 * the reference capture. The *shape* of the page is certain; the *wording* is a
 * careful reading of body copy that renders a few pixels tall. Every band that
 * carries prose therefore carries a `check`, exactly as `content/daimiServices.ts`
 * and `content/daimiTechnology.ts` do, and `daimiRetailChecks()` feeds them into
 * the same manifest as the rest of the site's amber notes so a production build
 * cannot ship them unread.
 *
 * **The one thing on this page that is decided rather than transcribed** is the
 * closing band's destination, and it was decided in the request: *"en aşağıda
 * önceden konuştuğumuz gibi 2024'e değil 2026 DAIMI report'a yönlendirelim"*. That
 * band is `content/reportClosing.ts`, shared by every report page but three, and it
 * already points at the Digital & AI Maturity Report 2026 — so this page inherits
 * the answer rather than restating it, and the reference capture agrees: its Global
 * Perspective card names the 2026 report too, and names this cut "The Retail Focus
 * Report", which is the `shortName` `focus()` already gives it.
 *
 * **What the live page has that this page cannot.** Its Download buttons open an
 * embedded HubSpot form — the fourth capture is that form, filled in. These
 * documents are prerendered with no client bundle at all (see `src/prerender.tsx`),
 * so an embedded form script would never run, and the other reports on this site
 * hand the reader the PDF from our own bucket instead. So every Download button
 * here is that PDF, and `DAIMI_RT_DOWNLOAD.check` is what keeps the question of
 * gating open.
 *
 * **Not to be confused with the Future 2030 Retail outlook**, which is a separate
 * report with its own slug, its own cover and its own designed page
 * (`content/outlookRetail.ts`, `pages/OutlookRetail.tsx`). Two reports, two pages,
 * one sector.
 */

/**
 * The report this page belongs to — the branch `pages/Reports.tsx` takes on it.
 *
 * The slug is unchanged from the entry `reports.ts` has listed since the index was
 * built: it is what digitopia.co serves, and tidying it would break the inbound
 * links this page exists to keep working.
 */
export const DAIMI_RT_SLUG = 'digital-and-ai-maturity-report-2026-retail-industry'

/**
 * The hero's two paragraphs, read off the reference capture.
 *
 * Two, not three — this cut does not close its hero with `FOCUS_CLOSE` the way the
 * Airlines and Banking pages do. The second paragraph is the sector's own
 * "to lead in 2026" line instead, and it is the page's argument in one sentence.
 *
 * The 3.0 in the first paragraph is set in bold on the live page. It is plain here,
 * because this page renders its paragraphs as strings rather than as markup — and
 * it is the one number the page can check against itself: 3.00 is also the 2025
 * average in Figure 1, and the sector landing exactly on the cross-industry
 * baseline is what the sentence is about.
 */
export const DAIMI_RT_INTRO = {
  body: [
    'Retail scores 3.0 in the 2026 DAIMI benchmark, aligning precisely with the cross-industry baseline. While competition has accelerated the digitization of customer journeys, overall maturity is weighed down by thin margins and fragmented data across complex ecosystems.',
    'To lead in 2026, retailers must move beyond adding tools to focus on disciplined execution, unifying customer and product data to turn local excellence into consistent, end-to-end operational performance.',
  ],
  /**
   * The report cover. It already existed as a 400px square — the size every card in
   * the reports grid carries — and the file attached with this request is the same
   * artwork much larger, so it replaced it under the same key rather than landing
   * beside it under a second name. The index card reads the same path and simply
   * gets a sharper cover out of it. Same call the Airlines, Automotive, Banking,
   * Construction, Utilities, Tourism, Technology and Services cuts made.
   *
   * **With the same caveat those covers had, file for file.** It arrived as two
   * captures, "(1 of 2)" and "(2 of 2)", and they are not two halves — they are
   * overlapping crops of the same square artwork, one cut off at the bottom through
   * the logo and one cut off at the top through the title's margin. The second is
   * the one held here, because it is the only one that contains the whole design; it
   * is 2000 × 1848 rather than square, so it is missing a sliver of the top margin,
   * and the index card's square crop trims about 4% off each side. `check` asks for
   * the file itself, which would replace this under the same key.
   */
  photo: {
    src: '/photo/daimi-report-2026-retail-cover.webp',
    alt: 'Digital & AI Maturity Report 2026: Retail Focus, February 2026',
  } satisfies Photo,
  check:
    'Three things. The two paragraphs are transcribed from the reference capture rather than supplied as text, so they want reading against the live page once before production — including the 3.0 in the first one, which the capture sets in bold and which is also Figure 1’s 2025 average. The live hero has no cover beside it — the right of the hero is empty but for the ribbon — and the cover is in the hero here because that is what the other designed report pages do; say if the live emptiness was the point. And the cover file itself is not whole: the two “scaled” captures you attached are overlapping crops of one square image, so the sharper one held here is 2000 × 1848 and loses a sliver of the margin above the title, with the index card trimming roughly 4% off each side to make it square again. Send the cover as a single file and it will replace this one under the same name.',
}

/**
 * The findings, and Figure 1 beside them.
 *
 * The live band is a lede paragraph followed by three lines that each open with a
 * bolded label — "Core Strengths:", "The Behind-the-Scenes Gap:", "Industrializing
 * AI:". Split into `label` and `body` here so the label can be set rather than
 * emphasised inside a sentence, which is what a `<strong>` in the middle of a
 * paragraph gives.
 *
 * Three points rather than the Services cut's four; the component reads the array,
 * so nothing about the band changes but its length.
 */
export const DAIMI_RT_FINDINGS = {
  heading: 'DAIMI Findings: Omnichannel Muscle vs. The Scaling Trap',
  lede: 'Retail maturity is strongest at the front end where interactions are visible, but often slows behind the scenes due to operational complexity.',
  points: [
    {
      label: 'Core Strengths',
      body: 'Retailers excel in Omnichannel Experience, Loyalty Ecosystems, and Digital Marketing, where they have built visible, measurable front-end capabilities.',
    },
    {
      label: 'The Behind-the-Scenes Gap',
      body: 'Progress is frequently hindered by Data Fragmentation and inconsistent execution across inventory accuracy, replenishment, and merchandising.',
    },
    {
      label: 'Industrializing AI',
      body: 'Leaders are operationalizing AI across planning, pricing, and promotions, while others remain stuck with isolated dashboards that do not translate into daily routines.',
    },
  ],
  /**
   * Figure 1 itself — the six-year study-results chart supplied with this request,
   * the same shape the Automotive, Banking and Services cuts carry rather than the
   * vitality scatter the Technology and Tourism cuts do or the portrait score range
   * the Utilities cut does. Six years, 2020 to 2025, each one a range from the
   * laggard through the average to the leader with that year's company count in the
   * header. It is 1200 × 605 on a white ground, so it gets a white card and fills it
   * whole.
   */
  photo: {
    src: '/photo/daimi-retail-study-results.webp',
    alt: 'DAIMI study results for the retail industry, 2020 to 2025: the laggard, average and leader score each year, from 1.75–2.50–3.30 in 2020 to 2.15–3.00–3.81 in 2025',
  } satisfies Photo,
  caption:
    'Figure 1: Digital & AI Maturity Index (DAIMI) Study Results: Retail Industry Score, Averages, Leaders, and Laggards',
  expertLabel: 'Talk to an expert',
  expertHref: LINKS.contact,
  check:
    'Figure 1 is the file you supplied and is shown whole, including its own year-by-year company counts (7 in 2020 rising to 152 in 2025) — the live caption carries neither those nor a year, so they are stated in the alt text and nowhere else. Its 2025 average, 3.00, is the same number the hero opens with, which is the one cross-check the page can do on itself; note that the leader line dips in 2025 (3.88 to 3.81) while the laggard rises sharply (1.83 to 2.15), which the prose does not mention either way. What wants a reading against digitopia.co is that prose: the lede and the three findings are transcribed from the capture, where body copy renders a few pixels tall.',
}

/** The analysis band, with the report mockup beside it. */
export const DAIMI_RT_ANALYSIS = {
  heading: 'A Comprehensive Analysis of Digital & AI Maturity in Retail',
  body: [
    'To lead in 2026, retail executives must connect digital growth engines to operational reliability. Success requires an aggressive focus on upskilling merchants, planners, and store leaders to ensure that AI-driven insights actually change behavior on the floor.',
    'Download the full report to access the detailed DAIMI benchmarks and the strategic roadmap to turn digital activity into a sustained, margin-protecting advantage.',
  ],
  /** The mockup supplied with this request — the retail cover, not another cut's. */
  photo: {
    src: '/photo/daimi-report-2026-retail-mockup.webp',
    alt: 'Two copies of the Digital & AI Maturity Report 2026 Retail Focus',
  } satisfies Photo,
  check:
    'Two things here. The paragraphs are transcribed from the reference capture rather than supplied as text, so they want reading against the live page once before production — the first one in particular, which is the page’s argument in one sentence. And on the live page this band is text only: the right half of that row is empty, and the report mockup you attached does not appear on the page anywhere. It is beside the analysis here because that is where the other designed focus cuts put theirs and because the file would otherwise go unused. Say if the live band’s emptiness was deliberate.',
}

/*
 * The methodology and global-perspective bands are not written out here. They are
 * `content/reportClosing.ts`, drawn by `components/ReportClosing.tsx` on every
 * report page but three — and the reference capture for this page shows exactly
 * that pair, pointing at the Digital & AI Maturity Report 2026, which is the "2024
 * değil 2026" the request asked for. Nothing to change: the shared band already
 * links to `DAIMI_SLUG`, and its opening sentence already names this cut "The
 * Retail Focus Report", word for word with the capture.
 */

/** The closing band, which is where the live page's gated form sits. */
export const DAIMI_RT_DOWNLOAD = {
  heading: 'Download Report',
  body: 'Get the Retail Industry Focus cut of the Digital & AI Maturity Report 2026.',
  /**
   * Resolved 2026-08-30. This band asked whether the report should sit behind
   * the form the live page gates it with, because every Download button here
   * linked straight to the bucket. It now does: the buttons carry
   * `data-gated-download` (`lib/gatedDownload.ts`), so the reader is asked for
   * an email at the foot of this page and `website-form-lead` sends the PDF to
   * it. There is no direct link left on the page to gate.
   */
  check: undefined,
}

/**
 * The page's open questions, in the shape `build-manifest.json` records copy checks
 * in — the same mechanism `daimiChecks()` and `daimiServicesChecks()` use, and
 * appended to the same list in `reportChecks()`.
 *
 * `'content'` rather than `'asset'`: nothing here is a missing file — this cut
 * arrived with both of its figures and its cover. Three of the notes are a
 * transcription nobody has read against the original, and one is a decision about
 * gating that belongs to marketing.
 */
export function daimiRetailChecks(): CheckItem[] {
  const sections: { section: string; check?: string }[] = [
    { section: 'hero', check: DAIMI_RT_INTRO.check },
    { section: 'findings', check: DAIMI_RT_FINDINGS.check },
    { section: 'analysis', check: DAIMI_RT_ANALYSIS.check },
    { section: 'download', check: DAIMI_RT_DOWNLOAD.check },
  ]
  return sections
    .filter((s): s is { section: string; check: string } => Boolean(s.check))
    .map((s) => ({
      locale: 'en',
      page: `impact-labs/reports/${DAIMI_RT_SLUG}`,
      section: s.section,
      text: s.check,
      severity: 'content',
    }))
}
